import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  LinearProgress
} from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  query,
  where
} from "firebase/firestore";
import { db } from "../firebase";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const colors = {
  darkGreen: '#06362D',
  calPolyGreen: '#155332',
  vanilla: '#DEE2B1',
  gold: '#FFD500',
  goldenrod: '#D1A505'
};

function Roomrates() {
  const auth = getAuth();

  const [userId, setUserId] = useState(null);
  const [propertyId, setPropertyId] = useState(null);

  const [rooms, setRooms] = useState([]);
  const [plans, setPlans] = useState({});
  const [roomPlans, setRoomPlans] = useState([]);
  const [rates, setRates] = useState({});
  const [discounts, setDiscounts] = useState({});
  const [savingRoomId, setSavingRoomId] = useState(null);

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      setUserId(user.uid);

      const snap = await getDocs(collection(db, "authroles"));
      snap.forEach(d => {
        if (d.id === user.uid) {
          setPropertyId(d.data().propertyId);
        }
      });
    });
    return () => unsub();
  }, []);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    if (!userId || !propertyId) return;
    loadAll();
  }, [userId, propertyId]);

  const loadAll = async () => {
    /* ROOMS */
    const roomSnap = await getDocs(
      query(
        collection(db, "rooms"),
        where("propertyId", "==", propertyId)
      )
    );
    setRooms(roomSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    /* RATE PLANS */
    const planSnap = await getDocs(collection(db, "rate_plans"));
    const planMap = {};
    planSnap.docs.forEach(d => {
      if (d.data().propertyId === propertyId || !d.data().propertyId) {
        planMap[d.id] = d.data().planName;
      }
    });
    setPlans(planMap);

    /* ROOM ↔ PLAN */
    const tariffSnap = await getDocs(
      query(
        collection(db, "room_tariffs"),
        where("propertyId", "==", propertyId)
      )
    );
    setRoomPlans(tariffSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    /* EXISTING FIXED RATES & DISCOUNTS */
    const rateSnap = await getDocs(
      query(
        collection(db, "roomrates"),
        where("propertyId", "==", propertyId)
      )
    );

    const rateMap = {};
    const discountMap = {};
    
    rateSnap.docs.forEach(d => {
      const r = d.data();
      const key = `${r.roomId}_${r.ratePlanId}`;
      rateMap[key] = r.rate;
      discountMap[key] = r.discount || "";
    });
    
    setRates(rateMap);
    setDiscounts(discountMap);
  };

  /* ================= CHANGE VALUE HANDLERS ================= */
  const changeRate = (roomId, planId, val) => {
    setRates(p => ({ ...p, [`${roomId}_${planId}`]: val }));
  };

  const changeDiscount = (roomId, planId, val) => {
    setDiscounts(p => ({ ...p, [`${roomId}_${planId}`]: val }));
  };

  /* ================= SAVE ================= */
  const saveRoom = async (roomId) => {
    try {
      setSavingRoomId(roomId);

      const plansForRoom = roomPlans.filter(p => p.roomId === roomId);

      for (let p of plansForRoom) {
        const key = `${roomId}_${p.ratePlanId}`;
        
        if (!rates[key] && !discounts[key]) continue;

        await setDoc(
          doc(db, "roomrates", key),
          {
            roomId,
            ratePlanId: p.ratePlanId,
            planName: plans[p.ratePlanId] || "Standard",
            rate: rates[key] ? Number(rates[key]) : 0,
            discount: discounts[key] ? Number(discounts[key]) : 0,
            userId,
            propertyId,
            updatedAt: new Date()
          }
        );
      }

      toast.success("Rates and discounts saved successfully");
    } catch (error) {
      console.error(error);
      toast.error("Save failed");
    } finally {
      setSavingRoomId(null);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
      <Box component="main" sx={{ flexGrow: 1, maxWidth: "1400px", margin: "0 auto" }}>
        <ToastContainer autoClose={2000} />

        <Typography variant="h5" fontWeight={800} mb={3} sx={{ color: colors.vanilla }}>
          Room Fixed Rates & Discounts
        </Typography>

        <Card sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
          <CardContent sx={{ p: 3 }}>
            <div className="container-fluid p-0">
              <div className="row">
                {rooms.map(room => {
                  const plansForRoom = roomPlans.filter(p => p.roomId === room.id);

                  return (
                    <div className="col-sm-6 mb-4" key={room.id}>
                      <Card
                        sx={{
                          borderRadius: 3,
                          boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                          border: `1px solid ${colors.goldenrod}`,
                          bgcolor: colors.darkGreen,
                          color: colors.vanilla,
                          height: "100%"
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          {/* Room Title */}
                          <Typography
                            fontWeight={800}
                            fontSize={16}
                            mb={2}
                            sx={{ color: colors.gold }}
                          >
                            {room.roomTypeName}
                          </Typography>

                          {/* Rate and Discount Rows */}
                          {plansForRoom.map((p) => {
                            const key = `${room.id}_${p.ratePlanId}`;
                            return (
                              <Box
                                key={p.id}
                                display="flex"
                                alignItems="center"
                                gap={2}
                                mb={1.5}
                                flexWrap="wrap"
                              >
                                {/* Plan Name */}
                                <Box
                                  width={140}
                                  fontWeight={600}
                                  sx={{ color: colors.vanilla, opacity: 0.9 }}
                                  noWrap
                                >
                                  {plans[p.ratePlanId] || "Plan"}
                                </Box>

                                {/* Rate Field */}
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Rate"
                                  InputLabelProps={{ style: { color: colors.vanilla } }}
                                  InputProps={{ style: { color: colors.vanilla } }}
                                  sx={{ 
                                    width: 110,
                                    "& .MuiOutlinedInput-root": { 
                                      backgroundColor: colors.calPolyGreen,
                                      "& fieldset": { borderColor: colors.goldenrod },
                                      "&:hover fieldset": { borderColor: colors.gold },
                                      "&.Mui-focused fieldset": { borderColor: colors.gold }
                                    } 
                                  }}
                                  value={rates[key] || ""}
                                  onChange={e => changeRate(room.id, p.ratePlanId, e.target.value)}
                                />

                                {/* Discount Field */}
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Discount"
                                  InputLabelProps={{ style: { color: colors.vanilla } }}
                                  InputProps={{ style: { color: colors.vanilla } }}
                                  sx={{ 
                                    width: 110,
                                    "& .MuiOutlinedInput-root": { 
                                      backgroundColor: colors.calPolyGreen,
                                      "& fieldset": { borderColor: colors.goldenrod },
                                      "&:hover fieldset": { borderColor: colors.gold },
                                      "&.Mui-focused fieldset": { borderColor: colors.gold }
                                    } 
                                  }}
                                  value={discounts[key] || ""}
                                  onChange={e => changeDiscount(room.id, p.ratePlanId, e.target.value)}
                                />
                              </Box>
                            );
                          })}

                          {/* Unified Room Action Button Section */}
                          <Box display="flex" justifyContent="flex-end" mt={2}>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={savingRoomId === room.id}
                              onClick={() => saveRoom(room.id)}
                              sx={{ 
                                minWidth: 120,
                                bgcolor: colors.goldenrod, 
                                color: colors.darkGreen, 
                                fontWeight: 700, 
                                textTransform: "none", 
                                borderRadius: "8px",
                                '&:hover': { bgcolor: colors.gold } 
                              }}
                            >
                              {savingRoomId === room.id ? "Saving..." : "Save Room Data"}
                            </Button>
                          </Box>

                          {/* Loader */}
                          {savingRoomId === room.id && (
                            <LinearProgress
                              sx={{
                                mt: 2,
                                borderRadius: 1,
                                backgroundColor: colors.calPolyGreen,
                                '& .MuiLinearProgress-bar': { backgroundColor: colors.gold }
                              }}
                            />
                          )}

                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Roomrates;